import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaShieldAlt, FaCode, FaNetworkWired, FaTools, FaBrain, FaGlobe } from 'react-icons/fa';
import './Skills.css';

const Skills = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const skillCategories = [
    {
      icon: FaShieldAlt,
      title: 'Cybersecurity',
      skills: [
        { name: 'Vulnerability Assessment', level: 85 },
        { name: 'Penetration Testing', level: 80 },
        { name: 'Incident Response', level: 88 },
        { name: 'Ethical Hacking', level: 82 },
        { name: 'Security Auditing', level: 89 },
        { name: 'PAM - Privileged Access Management (Krontech)', level: 90 }
      ]
    },
    {
      icon: FaCode,
      title: 'Programming',
      skills: [
        { name: 'Python', level: 92 },
        { name: 'C++', level: 85 },
        { name: 'C', level: 85 },
        { name: 'SQL', level: 70 },
        { name: 'Bash/Shell', level: 72 }
      ]
    },
    {
      icon: FaTools,
      title: 'Security Tools',
      skills: [
        { name: 'Metasploit', level: 89 },
        { name: 'Nmap', level: 95 },
        { name: 'Wireshark', level: 82 },
        { name: 'Burp Suite', level: 80 },
        { name: 'Nessus', level: 95 }
      ]
    },
    {
      icon: FaNetworkWired,
      title: 'Networking',
      skills: [
        { name: 'Network Protocols', level: 90 },
        { name: 'Firewall Configuration', level: 75 },
        { name: 'Network Analysis', level: 92 },
        { name: 'VPN Setup', level: 70 },
        { name: 'Network Security', level: 95 }
      ]
    },
    {
      icon: FaBrain,
      title: 'AI & Machine Learning',
      skills: [
        { name: 'Threat Detection AI', level: 97 },
        { name: 'Machine Learning', level: 99 },
        { name: 'Data Analysis', level: 85 },
        { name: 'Pattern Recognition', level: 82 },
        { name: 'Predictive Modeling', level: 75 }
      ]
    },
    {
      icon: FaGlobe,
      title: 'Web Technologies',
      skills: [
        { name: 'Web Security', level: 80 },
        { name: 'React.js', level: 75 },
        { name: 'Node.js', level: 70 },
        { name: 'HTML/CSS', level: 85 },
        { name: 'Web Vulnerabilities', level: 92 }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="skills" className="skills" ref={ref}>
      <div className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            Technical Skills
          </motion.h2>

          <div className="skills-grid">
            {skillCategories.map((category, categoryIndex) => (
              <motion.div
                key={category.title}
                className="skill-category"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <div className="category-header">
                  <motion.div 
                    className="category-icon"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <category.icon />
                  </motion.div>
                  <h3>{category.title}</h3>
                </div>

                <div className="skills-list">
                  {category.skills.map((skill, skillIndex) => (
                    <motion.div
                      key={skill.name}
                      className="skill-item"
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                      transition={{ 
                        delay: 0.5 + categoryIndex * 0.1 + skillIndex * 0.05,
                        duration: 0.6 
                      }}
                    >
                      <div className="skill-info">
                        <span className="skill-name">{skill.name}</span>
                        <span className="skill-percentage">{skill.level}%</span>
                      </div>
                      <div className="skill-bar">
                        <motion.div
                          className="skill-progress"
                          initial={{ width: 0 }}
                          animate={inView ? { width: `${skill.level}%` } : { width: 0 }}
                          transition={{ 
                            delay: 0.8 + categoryIndex * 0.1 + skillIndex * 0.05,
                            duration: 1,
                            ease: "easeOut"
                          }}
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div className="skills-summary" variants={itemVariants}>
            <h3>Key Strengths</h3>
            <div className="strengths-grid">
              {[
                {
                  title: 'Problem Solving',
                  description: 'Analytical thinking and creative solutions to complex security challenges'
                },
                {
                  title: 'Continuous Learning',
                  description: 'Staying updated with latest cybersecurity trends and technologies'
                },
                {
                  title: 'Team Collaboration',
                  description: 'Effective communication and teamwork in security projects'
                },
                {
                  title: 'Attention to Detail',
                  description: 'Meticulous approach to vulnerability assessment and code review'
                }
              ].map((strength, index) => (
                <motion.div
                  key={strength.title}
                  className="strength-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ delay: 1.2 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <h4>{strength.title}</h4>
                  <p>{strength.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;